import React, { useEffect, useMemo, useState } from 'react';

import { useProjectContext } from '../../contexts/ProjectContext';
import type { DiagnosisId } from '../../types/llm-diagnosis';
import styles from '../../assets/styles/components/AiDiagnosisPanelV2.module.scss';

type IssueSeverity = 'urgent' | 'attention' | 'review';

type IssueV2 = {
  issue_id: string;
  finding_ids: string[];
  platform: string | null;
  category: string;
  title: string;
  summary: string;
  what_happened: string;
  recommended_action: string;
  severity: IssueSeverity;
  status: 'open' | 'unknown';
  confidence: 'low' | 'medium' | 'high';
  priority_score: number;
  evidence: string[];
  data_gaps: string[];
  possible_factors: string[];
  time_axis: string | null;
  investigation_scopes: Array<{
    label: string;
    priority: string;
    reason: string;
    available_dimensions: string[];
  }>;
  action_plan: Array<{
    target: string;
    change: string;
    rationale: string;
    expected_effect: string;
    validation: string;
    confidence: string;
  }>;
};

type DiagnosisV2Response = {
  generated_at: string;
  project_id: number;
  issue_count: number;
  issues: IssueV2[];
};

interface AiDiagnosisPanelV2Props {
  activeDiagnosisId: DiagnosisId | null;
  setActiveDiagnosisId: (id: DiagnosisId | null) => void;
}

const SEVERITY_LABELS: Record<IssueSeverity, string> = {
  urgent: '緊急',
  attention: '注意',
  review: '確認',
};

const CONFIDENCE_LABELS: Record<IssueV2['confidence'], string> = {
  high: '高い',
  medium: '中程度',
  low: '低い',
};

const isIssueSeverity = (value: unknown): value is IssueSeverity =>
  value === 'urgent' || value === 'attention' || value === 'review';

const isConfidence = (value: unknown): value is IssueV2['confidence'] =>
  value === 'high' || value === 'medium' || value === 'low';

const asStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

const normalizeIssue = (value: unknown, index: number): IssueV2 => {
  const raw = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  return {
    issue_id: typeof raw.issue_id === 'string' ? raw.issue_id : `issue-${index}`,
    finding_ids: asStringArray(raw.finding_ids),
    platform: typeof raw.platform === 'string' ? raw.platform : null,
    category: typeof raw.category === 'string' ? raw.category : 'performance',
    title: typeof raw.title === 'string' ? raw.title : '診断結果を確認してください',
    summary: typeof raw.summary === 'string' ? raw.summary : '',
    what_happened: typeof raw.what_happened === 'string' ? raw.what_happened : '',
    recommended_action: typeof raw.recommended_action === 'string' ? raw.recommended_action : '',
    severity: isIssueSeverity(raw.severity) ? raw.severity : 'review',
    status: raw.status === 'open' ? 'open' : 'unknown',
    confidence: isConfidence(raw.confidence) ? raw.confidence : 'low',
    priority_score: typeof raw.priority_score === 'number' ? raw.priority_score : 0,
    evidence: asStringArray(raw.evidence),
    data_gaps: asStringArray(raw.data_gaps),
    possible_factors: asStringArray(raw.possible_factors),
    time_axis: typeof raw.time_axis === 'string' ? raw.time_axis : null,
    investigation_scopes: Array.isArray(raw.investigation_scopes)
      ? raw.investigation_scopes.filter((scope): scope is Record<string, unknown> => Boolean(scope && typeof scope === 'object')).map((scope) => ({
        label: typeof scope.label === 'string' ? scope.label : '関連する観点',
        priority: typeof scope.priority === 'string' ? scope.priority : 'medium',
        reason: typeof scope.reason === 'string' ? scope.reason : '',
        available_dimensions: asStringArray(scope.available_dimensions),
      }))
      : [],
    action_plan: Array.isArray(raw.action_plan)
      ? raw.action_plan.filter((plan): plan is Record<string, unknown> => Boolean(plan && typeof plan === 'object')).map((plan) => ({
        target: typeof plan.target === 'string' ? plan.target : '',
        change: typeof plan.change === 'string' ? plan.change : '',
        rationale: typeof plan.rationale === 'string' ? plan.rationale : '',
        expected_effect: typeof plan.expected_effect === 'string' ? plan.expected_effect : '',
        validation: typeof plan.validation === 'string' ? plan.validation : '',
        confidence: typeof plan.confidence === 'string' ? plan.confidence : 'low',
      }))
      : [],
  };
};

const normalizeDiagnosisResponse = (value: unknown): DiagnosisV2Response => {
  const raw = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  const rawIssues = Array.isArray(raw.issues) ? raw.issues : [];
  return {
    generated_at: typeof raw.generated_at === 'string' ? raw.generated_at : '',
    project_id: typeof raw.project_id === 'number' ? raw.project_id : 0,
    issue_count: typeof raw.issue_count === 'number' ? raw.issue_count : rawIssues.length,
    issues: rawIssues.map(normalizeIssue),
  };
};

const AiDiagnosisPanelV2: React.FC<AiDiagnosisPanelV2Props> = ({
  activeDiagnosisId,
  setActiveDiagnosisId,
}) => {
  const { selectedProjectId } = useProjectContext();
  const [diagnosis, setDiagnosis] = useState<DiagnosisV2Response | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDiagnosis = async (projectId: number) => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/v2/diagnosis/issues?project_id=${projectId}`,
          { credentials: 'include' },
        );
        if (!response.ok) throw new Error('V2診断データの取得に失敗しました。');
        const data: unknown = await response.json();
        setDiagnosis(normalizeDiagnosisResponse(data));
      } catch (fetchError) {
        console.error('V2診断データ取得エラー:', fetchError);
        setDiagnosis(null);
        setError('AI診断データの取得に失敗しました。時間をおいて再度お試しください。');
      } finally {
        setLoading(false);
      }
    };

    if (selectedProjectId) {
      void fetchDiagnosis(selectedProjectId);
    } else {
      setDiagnosis(null);
    }
  }, [selectedProjectId]);

  const visibleIssues = useMemo(() => diagnosis?.issues.slice(0, 5) ?? [], [diagnosis]);

  const handleIssueClick = (issueId: DiagnosisId) => {
    setActiveDiagnosisId(activeDiagnosisId === issueId ? null : issueId);
  };

  return (
    <section className={styles.diagnosisPanel}>
      <header className={styles.panelHeader}>
        <span>AI診断 V2</span>
        <span className={styles.updateTime}>
          {diagnosis?.generated_at ? `${new Date(diagnosis.generated_at).toLocaleString('ja-JP')}更新` : '-'}
        </span>
      </header>

      <div className={styles.panelContent}>
        <div className={styles.summaryHeader}>
          <div>
            <strong>今日確認すべきIssue</strong>
            <span>根拠の近い診断結果をまとめています</span>
          </div>
          <span className={styles.issueCount}>{diagnosis?.issue_count ?? 0}件</span>
        </div>

        {loading && <div className={styles.loadingText}>V2診断を生成中...</div>}
        {error && <div className={styles.errorText}>{error}</div>}
        {!loading && !error && visibleIssues.length === 0 && (
          <div className={styles.emptyText}>確認が必要なIssueはありません。</div>
        )}

        {!loading && !error && visibleIssues.length > 0 && (
          <div className={styles.diagnosisList}>
            {visibleIssues.map((issue) => {
              const isActive = activeDiagnosisId === issue.issue_id;
              return (
                <article
                  key={issue.issue_id}
                  className={`${styles.diagnosisCard} ${styles[issue.severity]} ${isActive ? styles.active : ''}`}
                  onClick={() => handleIssueClick(issue.issue_id)}
                >
                  <div className={styles.diagnosisCardHeader}>
                    <div className={styles.issueTitleWrap}>
                      <span className={`${styles.flagBadge} ${styles[issue.severity]}`}>
                        {SEVERITY_LABELS[issue.severity]}
                      </span>
                      <span className={styles.diagnosisTitle}>{issue.title}</span>
                    </div>
                    <span className={styles.priorityScore}>{issue.priority_score.toFixed(1)}</span>
                  </div>

                  {isActive && (
                    <div className={styles.diagnosisDetailCard} onClick={(event) => event.stopPropagation()}>
                      <h4 className={styles.detailTitle}>Issue詳細</h4>
                      <div className={styles.textSection}>
                        <div className={styles.sectionLabel}>起きたこと</div>
                        <p>{issue.what_happened}</p>
                        <p>{issue.summary}</p>
                      </div>
                      <div className={styles.textSection}>
                        <div className={styles.sectionLabel}>推奨アクション</div>
                        <p>{issue.recommended_action}</p>
                      </div>
                      {issue.action_plan.length > 0 && (
                        <div className={styles.textSection}>
                          <div className={styles.sectionLabel}>具体的な変更案</div>
                          {issue.action_plan.map((plan) => (
                            <div key={`${plan.target}-${plan.change}`}>
                              <p><strong>対象:</strong> {plan.target}</p>
                              <p><strong>変更:</strong> {plan.change}</p>
                              <p><strong>期待効果:</strong> {plan.expected_effect}</p>
                              <p><strong>検証:</strong> {plan.validation}</p>
                            </div>
                          ))}
                        </div>
                      )}
                      {issue.possible_factors.length > 0 && (
                        <div className={styles.textSection}>
                          <div className={styles.sectionLabel}>考えられる要因</div>
                          {issue.possible_factors.map((factor) => <p key={factor}>{factor}</p>)}
                        </div>
                      )}
                      <div className={styles.metaInfo}>
                        <div>
                          <strong>確信度:</strong> {CONFIDENCE_LABELS[issue.confidence]}
                        </div>
                        <div>
                          <strong>対象媒体:</strong> {issue.platform ?? '媒体共通'}
                        </div>
                        {issue.time_axis && (
                          <div>
                            <strong>時間軸:</strong> {issue.time_axis}
                          </div>
                        )}
                      </div>
                      {issue.evidence.length > 0 && (
                        <div className={styles.textSection}>
                          <div className={styles.sectionLabel}>根拠</div>
                          {issue.evidence.map((evidence) => <p key={evidence}>{evidence}</p>)}
                        </div>
                      )}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default AiDiagnosisPanelV2;
